import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Mobile WebTrader Layout
 * Bottom navigation with 4 tabs: Home, Markets, Positions, Funds
 * Each tab renders a different full-screen view
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { orderIntent } from '../utils/orderIntent';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { scopedQuote } from '../utils/quotes';
import { isBinancePair } from '../utils/binance';
import { useMarketClock } from '../hooks/useBinanceHistory';
import { watchLiveRefresh } from '../../hooks/liveRefresh';
import { 
  Home, BarChart3, Layers, Wallet, Bell, ChevronDown, Search, Star, ChevronRight, ChevronLeft, Clock,
  X, Banknote, ArrowUpCircle, ArrowDownCircle, Calendar, HelpCircle, Settings, Upload, 
  Sun, Moon, LogOut, Share2, Twitter, Linkedin, Facebook
} from 'lucide-react';
import InstrumentIcon from './InstrumentIcon';
import {AccountSwitcher,CustomerNotifications} from './CustomerAccountTools';
import {NotificationBadge} from '../notifications/NotificationContext';
import {AccessApprovalNotice} from './AccessApprovalNotice';
import {TraderConnection} from '../../components/connection/TraderConnection';
import {DepositWorkspace} from '../deposits/DepositWorkspace';
import {WithdrawalWorkspace} from '../withdrawals/WithdrawalWorkspace';
import NewOrderModal from './NewOrderModal';
import MobileInstrumentDetail from './MobileInstrumentDetail';
import { MobileCalendarView, MobileSettingsView } from './MobileViews';
import {CustomerMobileDocuments as MobileDocumentsView} from './CustomerMobileDocuments';
import TraderHelpCenter from '../pages/TraderHelpCenter';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../../components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Trading hours configuration - MUST MATCH backend trading_sessions.py MARKET_HOURS
const TRADING_HOURS = {
  forex: { hours: 'Mon-Fri', fullDesc: 'Monday 01:00 - Friday 23:59 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && !(d === 1 && h < 1); } },
  crypto: { hours: '24/7', fullDesc: 'Always open', isOpen: () => true },
  stocks: { hours: '14:30-21:00 UTC', fullDesc: 'Mon-Fri 14:30 - 21:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 14.5 && h < 21; } },
  stocks_us: { hours: '14:30-21:00 UTC', fullDesc: 'Mon-Fri 14:30 - 21:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 14.5 && h < 21; } },
  eu_stocks: { hours: '08:00-16:30 UTC', fullDesc: 'Mon-Fri 08:00 - 16:30 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 8.0 && (h + m/60) < 16.5; } },
  stocks_eu: { hours: '08:00-16:30 UTC', fullDesc: 'Mon-Fri 08:00 - 16:30 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(), m = new Date().getUTCMinutes(); return d >= 1 && d <= 5 && (h + m/60) >= 8.0 && (h + m/60) < 16.5; } },
  stocks_bist: { hours: '07:00-15:00 UTC', fullDesc: 'Mon-Fri 07:00 - 15:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && h >= 7 && h < 15; } },
  bist: { hours: '07:00-15:00 UTC', fullDesc: 'Mon-Fri 07:00 - 15:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return d >= 1 && d <= 5 && h >= 7 && h < 15; } },
  commodities: { hours: 'Sun 23:00 - Fri 22:00', fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return !(d === 6 || (d === 0 && h < 23) || (d === 5 && h >= 22)); } },
  indices: { hours: 'Sun 23:00 - Fri 22:00', fullDesc: 'Sunday 23:00 UTC - Friday 22:00 UTC', isOpen: () => { const d = new Date().getUTCDay(), h = new Date().getUTCHours(); return !(d === 6 || (d === 0 && h < 23) || (d === 5 && h >= 22)); } },
};

// Mobile Home View
const MobileHomeView = ({ 
  instruments, 
  favorites, 
  toggleFavorite, 
  onSelectInstrument,
  onInstrumentClick,
  formatPrice,
  user,
  isDark,
  t
}) => {
  const tx = useTraderText();
  const [activeTab, setActiveTab] = useState('popular');
  const tabs = [
    { id: 'popular', label: 'Most Popular' },
    { id: 'open', label: 'Open now' },
    { id: 'watchlist', label: 'My Watchlist' },
  ];

  const filteredInstruments = useMemo(() => {
    if (activeTab === 'watchlist') {
      return instruments.filter(i => favorites.includes(i.symbol));
    }
    if (activeTab === 'open') {
      // Show forex (24/5) and crypto (24/7) which are typically open
      return instruments.filter(i => i.category === 'forex' || i.category === 'crypto');
    }
    // Popular - show major forex, gold, bitcoin first
    const popularSymbols = ['EUR/USD', 'XAU/USD', 'BTC/USD', 'GBP/USD', 'USD/JPY', 'XAG/USD', 'ETH/USD', 'US100'];
    return instruments.sort((a, b) => {
      const aIdx = popularSymbols.indexOf(a.symbol);
      const bIdx = popularSymbols.indexOf(b.symbol);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    }).slice(0, 20);
  }, [instruments, activeTab, favorites]);

  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;

  return (
    <div className="flex flex-col h-full">
      {/* Greeting and Balance */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Hi, ")}<span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user?.full_name?.split(' ')[0] || 'Trader'}
            </span>
          </p>
        </div>
        <div className="text-right">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Your balance")}</p>
          <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {symbol}{balance.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className={`mx-4 p-1 rounded-full flex ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`mobile-home-tab-${tab.id}`}
            className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? isDark
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-900 shadow'
                : isDark
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {tx(tab.label)}
          </button>
        ))}
      </div>

      {/* Market Header */}
      <div className={`px-4 py-2 flex items-center justify-between text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <span>{tx("Market (")}{filteredInstruments.length}) ↕</span>
        <div className="flex gap-8">
          <span>{tx("Sell")}</span>
          <span>{tx("Buy")}</span>
        </div>
      </div>

      {/* Instrument List */}
      <div className="flex-1 overflow-y-auto">
        {filteredInstruments.map((inst) => (
          <MobileInstrumentRow
            key={inst.id || inst.symbol}
            inst={inst}
            isDark={isDark}
            isFavorite={favorites.includes(inst.symbol)}
            toggleFavorite={toggleFavorite}
            onSelect={() => onSelectInstrument(inst)}
            onTrade={() => onInstrumentClick(inst)}
            formatPrice={formatPrice}
          />
        ))}
      </div>
    </div>
  );
};

// Mobile Markets View
const MobileMarketsView = ({ 
  instruments, 
  favorites, 
  toggleFavorite,
  onSelectInstrument,
  onInstrumentClick,
  formatPrice,
  isDark,
  t
}) => {
  const tx = useTraderText();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'forex', label: 'Forex' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'indices', label: 'Indices' },
    { id: 'commodities', label: 'Commodities' },
    { id: 'stocks', label: 'US Stocks' },
    { id: 'eu_stocks', label: 'EU Stocks' },
    { id: 'bist', label: 'Turkish Equities' },
  ];

  const filteredInstruments = useMemo(() => {
    return instruments.filter(inst => {
      const matchesSearch = inst.symbol?.toLowerCase().includes(search.toLowerCase()) ||
                            inst.name?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = category === 'all' || 
                             (category === 'bist' && (inst.category === 'bist' || inst.tradingview_symbol?.startsWith('BIST:') || inst.symbol?.endsWith('.IS'))) ||
                             (category === 'eu_stocks' && (inst.category === 'eu_stocks' || inst.category === 'stocks_eu' || inst.symbol?.endsWith('.DE') || inst.symbol?.endsWith('.PA') || inst.symbol?.endsWith('.SW') || inst.symbol?.endsWith('.AS') || inst.symbol?.endsWith('.L') || inst.symbol?.endsWith('.MC') || inst.symbol?.endsWith('.MI'))) ||
                             (category === 'stocks' && (inst.category === 'stocks' || inst.category === 'stocks_us') && !['eu_stocks', 'stocks_eu'].includes(inst.category) && !inst.symbol?.endsWith('.IS') && !inst.symbol?.endsWith('.DE') && !inst.symbol?.endsWith('.PA') && !inst.symbol?.endsWith('.SW') && !inst.symbol?.endsWith('.AS') && !inst.symbol?.endsWith('.L') && !inst.symbol?.endsWith('.MC') && !inst.symbol?.endsWith('.MI')) ||
                             inst.category === category;
      return matchesSearch && matchesCategory;
    });
  }, [instruments, search, category]);

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filter */}
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2.5 rounded-full border ${
            isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <Search size={18} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tx("Search")}
              data-testid="mobile-market-search"
              className={`flex-1 w-full min-w-0 bg-transparent text-sm outline-none ${
                isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
          <select
            value={category}
            data-testid="mobile-market-category"
            onChange={(e) => setCategory(e.target.value)}
            className={`w-32 max-w-[40%] min-w-0 shrink-0 px-3 py-2.5 rounded-full border text-sm ${
              isDark 
                ? 'bg-gray-800/50 border-gray-700 text-white' 
                : 'bg-white border-gray-200 text-gray-900'
            }`}
          >
            {categories.map(c => (
              <option key={c.id} value={c.id}>{tx(c.label)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Market Header */}
      <div className={`px-4 py-2 flex items-center justify-between text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <span>{tx("Market (")}{filteredInstruments.length > 999 ? '1k+' : filteredInstruments.length}) ↕</span>
        <div className="flex gap-8">
          <span>{tx("Sell")}</span>
          <span>{tx("Buy")}</span>
        </div>
      </div>

      {/* Instrument List */}
      <div className="flex-1 overflow-y-auto">
        {filteredInstruments.map((inst) => (
          <MobileInstrumentRow
            key={inst.id || inst.symbol}
            inst={inst}
            isDark={isDark}
            isFavorite={favorites.includes(inst.symbol)}
            toggleFavorite={toggleFavorite}
            onSelect={() => onSelectInstrument(inst)}
            onTrade={() => onInstrumentClick(inst)}
            formatPrice={formatPrice}
          />
        ))}
      </div>
    </div>
  );
};

// Mobile Positions View
const MobilePositionsView = ({ user, positions, isDark, t }) => {
  const tx = useTraderText();
  const [activeTab, setActiveTab] = useState('open');
  const [confirming,setConfirming]=useState(null),[processing,setProcessing]=useState(false),[actionError,setActionError]=useState('');
  const submitAction=async()=>{
    if(!confirming||processing)return;
    setProcessing(true);setActionError('');
    try{
      const pending=confirming.status==='pending';
      const response=await fetch(`${API_BASE}/api/trader/${pending?`orders/${confirming.id}`:`positions/${confirming.id}/close`}`,{method:pending?'DELETE':'POST',headers:{Authorization:`Bearer ${localStorage.getItem('trader_token')}`,'Content-Type':'application/json'},body:pending?undefined:JSON.stringify({})});
      const result=await response.json();
      if(!response.ok)throw new Error(typeof result.detail==='string'?result.detail:'The trade could not be updated.');
      window.dispatchEvent(new Event('trader-data-changed'));toast.success(tx(pending?'Order cancelled':'Position closed'));setConfirming(null);
    }catch(error){setActionError(error.message);}finally{setProcessing(false);}
  };
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('time');

  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;
  const credit = user?.credit || 0;
  const equity = user?.equity ?? balance + credit;
  const usedMargin = user?.used_margin ?? 0;
  const freeMargin = user?.free_margin ?? equity - usedMargin;

  const tabs = [
    { id: 'open', label: 'Open', count: positions?.filter(p => p.status === 'open').length || 0 },
    { id: 'pending', label: 'Pending' },
    { id: 'closed', label: 'Closed' },
  ];

  const filteredPositions = useMemo(() => {
    let result = positions || [];
    if (activeTab === 'open') result = result.filter(p => p.status === 'open');
    else if (activeTab === 'pending') result = result.filter(p => p.status === 'pending');
    else result = result.filter(p => p.status === 'closed');
    
    if (search) {
      result = result.filter(p => p.symbol?.toLowerCase().includes(search.toLowerCase()));
    }
    return result;
  }, [positions, activeTab, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Financial Summary Bar */}
      <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        <div className="flex-1 border-r border-gray-700 pr-3">
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Equity")}</p>
          <p data-testid="mobile-positions-equity" className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {symbol}{equity.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex-1 border-r border-gray-700 px-3">
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Free margin")}</p>
          <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {symbol}{freeMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex-1 px-3">
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Used margin")}</p>
          <p data-testid="mobile-positions-used-margin" className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {symbol}{usedMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <ChevronRight size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
      </div>

      {/* Tabs */}
      <div className={`mx-4 mt-4 p-1 rounded-full flex ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`mobile-positions-tab-${tab.id}`}
            className={`flex-1 py-2 px-3 rounded-full text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
              activeTab === tab.id
                ? 'bg-cyan-500 text-white'
                : isDark
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {tx(tab.label)}
            {tab.count !== undefined && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                activeTab === tab.id ? 'bg-white/20' : isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search and Sort */}
      <div className="px-4 py-3 flex gap-2">
        <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-full border ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <Search size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tx("Search")}
            className={`flex-1 bg-transparent text-sm outline-none ${
              isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
            }`}
          />
        </div>
        <div className="text-right">
          <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Sort by")}</p>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`bg-transparent text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            <option value="time">{tx("Open time")}</option>
            <option value="symbol">{tx("Symbol")}</option>
            <option value="pnl">{tx("P/L")}</option>
          </select>
        </div>
      </div>

      {/* Positions List */}
      <div className="flex-1 overflow-y-auto px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        {filteredPositions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="w-32 h-32 mb-4 flex items-center justify-center">
              <svg viewBox="0 0 120 120" className="w-full h-full">
                <circle cx="60" cy="80" r="25" fill="#3B82F6" opacity="0.2"/>
                <rect x="40" y="30" width="40" height="50" rx="4" fill="#60A5FA"/>
                <rect x="50" y="40" width="20" height="4" rx="2" fill="white"/>
                <rect x="50" y="50" width="15" height="4" rx="2" fill="white"/>
                <circle cx="85" cy="45" r="15" fill="#F59E0B"/>
                <path d="M80 45 L85 50 L95 40" stroke="white" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("No items to display")}</p>
          </div>
        ) : (
          filteredPositions.map((pos) => (
            <div 
              key={pos.id} 
              data-testid={`mobile-position-${pos.id}`}
              className={`py-3 px-4 mb-2 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-white shadow-sm'}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pos.symbol}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      (pos.side || pos.direction) === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {tx((pos.side || pos.direction)?.toUpperCase())}
                    </span>
                  </div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span>{tx("Vol: ")}{pos.volume || pos.quantity}</span>
                    <span className="mx-2">|</span>
                    <span data-testid={`mobile-trade-price-${pos.id}`}>@ {Number(pos.open_price ?? pos.entry_price ?? pos.price ?? 0).toFixed(5)}</span>
                  </div>
                </div>
                <div className="text-right">{pos.status==='pending'?<span data-testid={`mobile-trade-status-${pos.id}`} className={`text-xs ${isDark?'text-gray-400':'text-gray-500'}`}>{tx("Pending ")}{tx(pos.type||pos.order_type)}</span>:<>
                  <p className={`font-bold text-lg ${(pos.pnl || pos.profit_loss || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(pos.pnl || pos.profit_loss || 0) >= 0 ? '+' : ''}{symbol}{(pos.pnl || pos.profit_loss || 0).toFixed(2)}
                  </p>
                  <p className={`text-xs ${(pos.pnl || pos.profit_loss || 0) >= 0 ? 'text-green-500/70' : 'text-red-500/70'}`}>
                    {(pos.pnl_pct ?? 0).toFixed(2)}%
                  </p>
                  </>}
                </div>
              </div>
              {/* Close button - only for open positions */}
              {(pos.status === 'open'||pos.status==='pending') && (
                <button
                  onClick={() => {setActionError('');setConfirming(pos);}}
                  data-testid={`mobile-${pos.status==='pending'?'cancel':'close'}-${pos.id}`}
                  className={`mt-3 w-full py-2.5 rounded-lg text-sm font-medium transition-all active:scale-98 ${
                    isDark ? 'bg-gray-700 text-white active:bg-gray-600' : 'bg-gray-100 text-gray-900 active:bg-gray-200'
                  }`}
                >
                  {tx(pos.status==='pending'?'Cancel Order':'Close Position')}
                </button>
              )}
            </div>
          ))
        )}
      </div>
      <AlertDialog open={!!confirming} onOpenChange={open=>{if(!open&&!processing)setConfirming(null);}}><AlertDialogContent data-testid="mobile-trade-confirm" className={isDark?'bg-gray-900 text-white border-gray-700':''}><AlertDialogHeader><AlertDialogTitle data-testid="mobile-trade-confirm-title">{tx(confirming?.status==='pending'?'Cancel order':'Close position')} — {confirming?.symbol}</AlertDialogTitle><AlertDialogDescription>{tx(confirming?.status==='pending'?'This pending order will be cancelled.':'This position will close at the available server quote.')}</AlertDialogDescription></AlertDialogHeader>{actionError&&<p role="alert" data-testid="mobile-trade-confirm-error" className="text-red-500 text-sm">{tx(actionError)}</p>}<AlertDialogFooter><AlertDialogCancel data-testid="mobile-trade-confirm-cancel" disabled={processing}>{tx("Keep trade")}</AlertDialogCancel><AlertDialogAction data-testid="mobile-trade-confirm-submit" disabled={processing} onClick={event=>{event.preventDefault();submitAction();}}>{tx(processing?'Processing…':confirming?.status==='pending'?'Cancel order':'Close position')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

// Mobile Funds View
const MobileFundsView = ({ user, positions, isDark, t, onNavigate }) => {
  const tx = useTraderText();
  const currency = user?.currency || 'TRY';
  const symbol = currency === 'TRY' ? '₺' : '$';
  const balance = user?.balance || 0;
  const credit = user?.credit || 0;
  const openPnL = user?.open_pl ?? 0;
  const equity = user?.equity ?? balance + credit + openPnL;
  const usedMargin = user?.used_margin ?? 0;
  const freeMargin = user?.free_margin ?? equity - usedMargin;

  const exposureByCategory={forex:0,commodities:0,other:0};
  (positions||[]).filter(p=>p.status==='open').forEach(p=>{
    const category=Object.hasOwn(exposureByCategory,p.category)?p.category:'other';
    exposureByCategory[category]+=Math.abs((p.quantity??p.volume??0)*(p.contract_size??1)*(p.current_price??p.entry_price??0));
  });
  const totalExposure=Object.values(exposureByCategory).reduce((sum,value)=>sum+value,0);
  let allocationOffset=0;
  const allocations=[['forex','FX','#06b6d4'],['commodities','Commodities','#F59E0B'],['other','Other','#94a3b8']].map(([key,name,color])=>{
    const percent=totalExposure?exposureByCategory[key]/totalExposure*100:0;
    const item={name,color,percent,offset:allocationOffset};allocationOffset+=percent;return item;
  });

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Asset Allocation Header */}
      <div className="px-4 pt-4 flex items-center justify-between">
        <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Asset Allocation")}</h2>
      </div>

      {/* Donut Chart */}
      <div className="px-4 py-6 flex flex-col items-center">
        <div className="relative w-48 h-32">
          <svg viewBox="0 0 200 120" className="w-full h-full">
            <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={isDark?'#374151':'#e5e7eb'} strokeWidth="16"/>
            {allocations.filter(a=>a.percent>0).map(a=><path key={a.name} d="M 20 100 A 80 80 0 0 1 180 100" pathLength="100" fill="none" stroke={a.color} strokeWidth="16" strokeDasharray={`${a.percent} ${100-a.percent}`} strokeDashoffset={-a.offset}/>)}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Open exposure")}</p>
            <p data-testid="mobile-funds-exposure" className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{totalExposure.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex gap-4 mt-2">
          {allocations.map((a) => (
            <div key={a.name} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: a.color }}></div>
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {tx(a.name)} {a.percent.toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Account Balance */}
      <div className="px-4 pb-8">
        <h3 className={`text-base font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Account Balance")}</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Open P/L")}</span>
            <span data-testid="mobile-funds-open-pl" className={`font-medium ${openPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {symbol}{openPnL.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Balance")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{balance.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Equity")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{equity.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Used margin")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{usedMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Free margin")}</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {symbol}{freeMargin.toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Instrument Row Component
const MobileInstrumentRow = ({ inst, isDark, isFavorite, toggleFavorite, onSelect, onTrade, formatPrice }) => {
  const tx = useTraderText();
  const marketStatus = TRADING_HOURS[inst.category] || TRADING_HOURS.forex;
  const isOpen = inst.is_virtual ? inst.session_status==='open' : marketStatus.isOpen();
  
  // Get mid price for display
  const midPrice = inst.bid && inst.ask ? (inst.bid + inst.ask) / 2 : inst.bid || inst.ask || 0;
  const change = inst.change || 0;
  const high = inst.high || (midPrice * 1.005);
  const low = inst.low || (midPrice * 0.995);
  
  return (
    <div
      onClick={onSelect}
      data-testid={`mobile-instrument-${inst.symbol?.replace('/', '-')}`}
      className={`mx-3 mb-2 px-4 py-3 rounded-xl cursor-pointer active:scale-[0.99] transition-transform ${
        isDark ? 'bg-gray-800/50' : 'bg-white shadow-sm border border-gray-100'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <InstrumentIcon symbol={inst.symbol} category={inst.category} tradingview_symbol={inst.tradingview_symbol} size={32} isDark={isDark} />
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{inst.symbol}</span>
            <button 
              onClick={(e) => { e.stopPropagation(); toggleFavorite(inst.symbol, e); }}
              className="p-0.5"
            >
              <Star size={14} className={isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'} />
            </button>
            <span className={`text-xs flex items-center gap-1 ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
            </span>
          </div>
          <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{inst.name || inst.symbol}</p>
        </div>

        {/* Price Column */}
        <div className="text-right">
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {formatPrice(midPrice, inst.symbol)}
          </p>
          <div className="flex gap-3 text-[10px]">
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("H: ")}{formatPrice(high, inst.symbol)}
            </span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("L: ")}{formatPrice(low, inst.symbol)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Mobile Layout Component
const MobileTraderLayout = () => {
  const tx = useTraderText();
  const { isDark, toggleTheme } = useTraderTheme();
  const { t, language, setLanguage } = useTraderLanguage();
  const { user, logout } = useTraderAuth();
  const { prices, positions: wsPositions, subscribe, isConnected, requestPositions } = useWebSocket();
  
  const [activeTab, setActiveTab] = useState('home');
  const [instruments, setInstruments] = useState([]);
  const [favorites, setFavorites] = useState(['EUR/USD', 'XAU/USD', 'BTC/USD']);
  const [loading, setLoading] = useState(true);
  const quoteClock = useMarketClock(instruments.some(inst=>!inst.is_virtual&&isBinancePair(inst.symbol)));
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showCustomerNotifications, setShowCustomerNotifications] = useState(false);
  const [showInstrumentDetail, setShowInstrumentDetail] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showHelpSubmenu, setShowHelpSubmenu] = useState(false);
  const [apiPositions, setApiPositions] = useState([]);
  
  // Views state for sidebar navigation
  const [currentView, setCurrentView] = useState(null); // null, 'deposit', 'withdraw', 'calendar', 'settings', 'documents'
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  
  // Fetch positions from API (more reliable than WebSocket for initial load)
  useEffect(() => {
    let active=true;
    const fetchPositions = async () => {
      try {
        const token = localStorage.getItem('trader_token');
        if (!token) return;
        
        const [response,orders]=await Promise.all([fetch(`${API_BASE}/api/trader/positions?status=`,{headers:{Authorization:`Bearer ${token}`}}),fetch(`${API_BASE}/api/trader/orders?status=pending`,{headers:{Authorization:`Bearer ${token}`}})]);
        if(response.ok&&orders.ok){const [positions,pending]=await Promise.all([response.json(),orders.json()]);if(active&&localStorage.getItem('trader_token')===token)setApiPositions([...positions,...pending]);}
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      }
    };
    
    fetchPositions();
    const interval = setInterval(fetchPositions, 5000);
    window.addEventListener('trader-data-changed',fetchPositions);
    return () => {active=false;clearInterval(interval);window.removeEventListener('trader-data-changed',fetchPositions);};
  }, []);
  
  // Also request positions via WebSocket when connected
  useEffect(() => {
    if (isConnected && requestPositions) {
      requestPositions();
    }
  }, [isConnected, requestPositions]);
  
  const positions = apiPositions;

  // Language options
  const languageOptions = [
    { code: 'en', flag: '🇬🇧', name: 'English' },
    { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  ];

  const currentLangOption = languageOptions.find(l => l.code === language) || languageOptions[0];

  // Fetch instruments
  useEffect(() => {
    const fetchInstruments = async (silent=false) => {
      try {
        if(!silent)setLoading(true);
        const token = localStorage.getItem('trader_token');
        const response = await fetch(`${API_BASE}${token ? '/api/trader/instruments' : '/api/instruments'}?limit=1000`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Known prices for common instruments
          const knownPrices = {
            'EUR/USD': 1.0842, 'EURUSD': 1.0842,
            'GBP/USD': 1.2634, 'GBPUSD': 1.2634,
            'USD/JPY': 149.52, 'USDJPY': 149.52,
            'USD/CHF': 0.8845, 'USDCHF': 0.8845,
            'AUD/USD': 0.6523, 'AUDUSD': 0.6523,
            'USD/CAD': 1.3567, 'USDCAD': 1.3567,
            'NZD/USD': 0.5987, 'NZDUSD': 0.5987,
            'EUR/GBP': 0.8580, 'EURGBP': 0.8580,
            'EUR/JPY': 162.15, 'EURJPY': 162.15,
            'GBP/JPY': 188.90, 'GBPJPY': 188.90,
            'AUD/NZD': 1.0890, 'AUDNZD': 1.0890,
            'EUR/CHF': 0.9590, 'EURCHF': 0.9590,
            'EUR/AUD': 1.6620, 'EURAUD': 1.6620,
            'EUR/CAD': 1.4710, 'EURCAD': 1.4710,
            'USD/TRY': 38.50, 'USDTRY': 38.50,
            'EUR/TRY': 41.75, 'EURTRY': 41.75,
            'XAU/USD': 4508.00, 'XAUUSD': 4508.00,
            'XAG/USD': 28.50, 'XAGUSD': 28.50,
            'XPT/USD': 985.00, 'XPTUSD': 985.00,
            'USOIL': 82.50,
            'UKOIL': 86.30,
            'NATGAS': 2.85,
            'BTC/USD': 67500, 'BTCUSD': 67500,
            'ETH/USD': 3450, 'ETHUSD': 3450,
            'XRP/USD': 0.52, 'XRPUSD': 0.52,
            'SOL/USD': 145, 'SOLUSD': 145,
            'ADA/USD': 0.45, 'ADAUSD': 0.45,
            'DOGE/USD': 0.12, 'DOGEUSD': 0.12,
            'US100': 18500,
            'US500': 5200,
            'US30': 39500,
            'GER40': 18200,
            'UK100': 8150,
            'FRA40': 7950,
            'JP225': 38500,
            'HK50': 17800,
            'AU200': 7850,
            // BIST Indices (TRY)
            'XU100': 10250.0, 'XU030': 10850.0, 'XUBANK': 4250.0, 'XUSIN': 8750.0,
            // Major BIST stocks (TRY)
            'GARAN': 85.50, 'AKBNK': 62.30, 'ISCTR': 18.75, 'VAKBN': 24.60,
            'THYAO': 315.00, 'EREGL': 58.40, 'KCHOL': 195.00, 'SAHOL': 78.50,
            'TUPRS': 185.00, 'ASELS': 95.00, 'SISE': 52.80, 'TCELL': 88.50,
            'BIMAS': 465.00, 'FROTO': 1250.00, 'TOASO': 385.00, 'ARCLK': 195.00,
            'PGSUS': 875.00, 'HALKB': 22.50, 'YKBNK': 32.40, 'TTKOM': 48.60,
          };
          
          const transformed = data.map(inst => {
            const symbol = inst.symbol || '';
            const cleanSymbol = symbol.replace('/', '').replace('.IS', '').toUpperCase();
            const category = inst.category || inst.type || 'stocks';
            const isBIST = category === 'bist' || inst.tradingview_symbol?.startsWith('BIST:') || symbol.endsWith('.IS');
            
            // Get base price from known prices or calculate based on category
            let basePrice = knownPrices[symbol] || knownPrices[cleanSymbol] || null;
            
            if (basePrice === null) {
              if (category === 'forex') {
                // Forex pairs typically trade around 1.x for majors, 100+ for JPY pairs
                if (symbol.includes('JPY') || symbol.includes('TRY')) {
                  basePrice = symbol.includes('TRY') ? 35 + Math.random() * 10 : 140 + Math.random() * 20;
                } else {
                  basePrice = 0.8 + Math.random() * 0.6; // 0.8 to 1.4
                }
              } else if (category === 'crypto') {
                basePrice = 100 + Math.random() * 5000;
              } else if (category === 'commodities') {
                basePrice = 50 + Math.random() * 50;
              } else if (category === 'indices') {
                basePrice = 5000 + Math.random() * 15000;
              } else if (isBIST) {
                // Turkish stocks - TRY denominated, typically 20-500 TRY range
                basePrice = 50 + Math.random() * 200;
              } else {
                // General stocks
                basePrice = 50 + Math.random() * 150;
              }
            }
            
            // Calculate spread based on instrument type
            let spreadPercent = 0.0003; // Default 3 pips
            if (category === 'crypto') spreadPercent = 0.005;
            else if (category === 'indices') spreadPercent = 0.0005;
            else if (category === 'commodities') spreadPercent = 0.001;
            else if (category === 'bist') spreadPercent = 0.002;
            
            const spread = basePrice * spreadPercent;
            
            return {
              ...inst,
              id: inst.id,
              symbol: inst.symbol,
              name: inst.name,
              bid: 0,
              ask: 0,
              change: 0,
              category: category,
              tradingview_symbol: inst.tradingview_symbol,
            };
          });
          
          setInstruments(transformed);
          subscribe(transformed.map(i => i.symbol).filter(Boolean));
        }
      } catch (error) {
        console.error('Failed to fetch instruments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInstruments();
    return watchLiveRefresh(()=>fetchInstruments(true));
  }, [subscribe]);

  // Update with live prices
  const instrumentsWithPrices = useMemo(() => {
    return instruments.map(inst => {
      const live = scopedQuote(inst, prices[inst.symbol]);
      if (live) {
        return { ...inst, bid: live.bid, ask: live.ask, session_status: live.session_status, quote_source:live.source, quote_available:live.quote_available, is_live_market:live.is_live_market, change: live.change_pct || inst.change };
      }
      return !inst.is_virtual&&isBinancePair(inst.symbol)?{...inst,bid:0,ask:0,price:0,quote_source:'binance',is_live_market:true,quote_available:false}:inst;
    });
  }, [instruments, prices, quoteClock]);

  const toggleFavorite = useCallback((symbol, e) => {
    e?.stopPropagation();
    setFavorites(prev => 
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  }, []);

  const formatPrice = useCallback((price, symbol) => {
    if (!price || isNaN(price)) return '0.00';
    if (price > 1000) return price.toFixed(2);
    if (price > 10) return price.toFixed(2);
    return price.toFixed(5);
  }, []);

  const handleInstrumentClick = (inst) => {
    setSelectedInstrument(inst);
    setShowInstrumentDetail(true);
  };

  const orderKey=useRef(null);
  const handleOpenPosition = async (orderData) => {
    try {
      const token = localStorage.getItem('trader_token');
      const response = await fetch(`${API_BASE}/api/trader/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderIntent(orderKey,{
          symbol: orderData.instrument.symbol,
          side: orderData.side,
          volume: Number(orderData.size),
          type: orderData.orderType || 'market',
          price: orderData.pendingPrice,
          stop_loss: orderData.stopLoss,
          take_profit: orderData.takeProfit,
        })),
      });
      
      if (response.ok) {
        toast.success(tx(`${orderData.side.toUpperCase()} ${orderData.size} ${orderData.instrument.symbol} ${orderData.orderType==='pending'?'pending':'executed'}`));
        orderKey.current=null;
        window.dispatchEvent(new Event('trader-data-changed'));
        setShowInstrumentDetail(false);
        return true;
      } else {
        const error = await response.json();
        toast.error(tx(Array.isArray(error.detail)?error.detail.map(item=>item.msg).join('; '):error.detail || 'Order failed'));
        return false;
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(tx('Failed to place order'));
      return false;
    }
  };

  const tabs = [
    { id: 'home', icon: Home, label: 'Home' },
    { id: 'markets', icon: BarChart3, label: 'Markets' },
    { id: 'positions', icon: Layers, label: 'Positions' },
    { id: 'funds', icon: Wallet, label: 'Funds' },
  ];

  const accountId = user?.account_id || user?.id?.slice(0, 8).toUpperCase() || 'N/A';
  const currency = user?.currency || 'USD';

  // Render full-screen views from sidebar navigation
  if (currentView === 'deposit') {
    return <div className="tr-deposit-mobile-shell"><header className="tr-deposit-mobile-back"><button data-testid="mobile-deposit-back" onClick={()=>setCurrentView(null)}><ChevronLeft size={20}/>{tx(language==='tr'?'Geri':'Back')}</button></header><TraderConnection/><DepositWorkspace/></div>;
  }
  if (currentView === 'withdraw') {
    return <div className="tr-deposit-mobile-shell"><header className="tr-deposit-mobile-back"><button data-testid="mobile-withdraw-back" onClick={()=>setCurrentView(null)}><ChevronLeft size={20}/>{tx(language==='tr'?'Geri':'Back')}</button></header><TraderConnection/><WithdrawalWorkspace/></div>;
  }
  if (currentView === 'calendar') {
    return <MobileCalendarView onBack={() => setCurrentView(null)} />;
  }
  if (currentView === 'settings') {
    return <MobileSettingsView onBack={() => setCurrentView(null)} />;
  }
  if (currentView === 'documents') {
    return <MobileDocumentsView onBack={() => setCurrentView(null)} />;
  }
  if (currentView === 'help') {
    return (
      <div className="tr-deposit-mobile-shell">
        <header className="tr-deposit-mobile-back">
          <button data-testid="mobile-help-back" onClick={() => setCurrentView(null)}>
            <ChevronLeft size={20} />
            {tx(language === 'tr' ? 'Geri' : 'Back')}
          </button>
        </header>
        <TraderConnection />
        <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <TraderHelpCenter />
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`px-4 py-3 flex items-center justify-between ${isDark ? 'bg-[#0f1724]' : 'bg-white'}`}>
        {/* Profile Avatar - Opens Sidebar */}
        <button 
          onClick={() => setShowSidebar(true)}
          data-testid="mobile-user-menu-toggle"
          className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
            isDark ? 'border-cyan-500 text-cyan-500' : 'border-blue-500 text-blue-500'
          }`}
        >
          <span className="font-bold">{user?.full_name?.[0] || 'T'}</span>
        </button>

        {/* Account Info - Shows Account ID */}
        <button
          onClick={() => setShowAccountDropdown(!showAccountDropdown)}
          data-testid="mobile-account-switch-toggle"
          className={`px-3 py-1.5 rounded-lg border flex flex-col items-center ${
            isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Account")}</span>
          <div className="flex items-center gap-1.5">
            <span className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{accountId}</span>
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>| {currency}</span>
            <ChevronDown size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          </div>
        </button>

        {/* Notifications */}
        <button data-testid="mobile-notifications-toggle" aria-label={tx("Notifications")} onClick={()=>setShowCustomerNotifications(!showCustomerNotifications)} className={`relative p-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <Bell size={24} />
          <NotificationBadge id="mobile-notification-unread-badge"/>
        </button>
      </header>
      <AccessApprovalNotice/>
      <TraderConnection/>
      {showAccountDropdown&&<div className={`mx-4 border rounded max-h-60 overflow-y-auto ${isDark?'bg-gray-900 border-gray-700':'bg-white border-gray-200'}`}><AccountSwitcher onClose={()=>setShowAccountDropdown(false)}/></div>}
      {showCustomerNotifications&&<><button type="button" className="mobile-inbox-backdrop" data-testid="mobile-inbox-dismiss" aria-label={tx("Close notifications")} onClick={()=>setShowCustomerNotifications(false)}/><div data-testid="mobile-inbox-panel" className={`mobile-inbox-panel border ${isDark?'bg-gray-900 border-gray-700':'bg-white border-gray-200'}`}><CustomerNotifications/></div></>}

      {/* Content Area */}
      <main className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {activeTab === 'home' && (
              <MobileHomeView
                instruments={instrumentsWithPrices}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                onSelectInstrument={handleInstrumentClick}
                onInstrumentClick={handleInstrumentClick}
                formatPrice={formatPrice}
                user={user}
                isDark={isDark}
                t={t}
              />
            )}
            {activeTab === 'markets' && (
              <MobileMarketsView
                instruments={instrumentsWithPrices}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
                onSelectInstrument={handleInstrumentClick}
                onInstrumentClick={handleInstrumentClick}
                formatPrice={formatPrice}
                isDark={isDark}
                t={t}
              />
            )}
            {activeTab === 'positions' && (
              <MobilePositionsView
                user={user}
                positions={positions}
                isDark={isDark}
                t={t}
              />
            )}
            {activeTab === 'funds' && (
              <MobileFundsView
                user={user}
                positions={positions}
                isDark={isDark}
                t={t}
              />
            )}
          </>
        )}
      </main>

      {/* Bottom Navigation - with extra padding for Emergent badge */}
      <nav className={`flex items-center justify-around py-3 border-t safe-area-inset-bottom ${
        isDark ? 'bg-[#0f1724] border-gray-800' : 'bg-white border-gray-200'
      }`} style={{ paddingBottom: '48px', marginBottom: '0px' }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                console.log('Tab clicked:', tab.id);
                setActiveTab(tab.id);
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all active:scale-95 ${
                isActive
                  ? isDark ? 'text-cyan-400 bg-cyan-500/10' : 'text-cyan-600 bg-cyan-50'
                  : isDark ? 'text-gray-500 active:text-gray-300' : 'text-gray-400 active:text-gray-600'
              }`}
              data-testid={`mobile-nav-${tab.id}`}
            >
              <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
              <span className={`text-[10px] font-medium ${isActive ? 'font-bold' : ''}`}>{tx(tab.label)}</span>
            </button>
          );
        })}
      </nav>

      {/* Order Modal */}
      <NewOrderModal
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        instrument={selectedInstrument}
      />

      {/* Mobile Instrument Detail View */}
      {showInstrumentDetail && selectedInstrument && (
        <MobileInstrumentDetail
          instrument={selectedInstrument}
          onClose={() => setShowInstrumentDetail(false)}
          onOpenPosition={handleOpenPosition}
          formatPrice={formatPrice}
          isFavorite={favorites.includes(selectedInstrument.symbol)}
          toggleFavorite={toggleFavorite}
        />
      )}

      {/* Mobile Sidebar Menu */}
      {showSidebar && (
        <div className="fixed inset-0 z-[200] flex" data-testid="mobile-trader-sidebar" role="dialog" aria-modal="true" aria-label={tx('Menu')}>
          {/* Sidebar Panel */}
          <div 
            className={`w-[85%] max-w-[320px] h-full flex flex-col ${
              isDark ? 'bg-[#0f1724]' : 'bg-white'
            }`}
            style={{ animation: 'slideInLeft 0.3s ease-out' }}
          >
            {/* Header with Logo and Language */}
            <div className={`px-5 py-4 flex items-center justify-between border-b ${
              isDark ? 'border-gray-800' : 'border-gray-200'
            }`}>
              {/* Logo/Brand */}
              <div className={`px-4 py-2 rounded-full border-2 ${
                isDark ? 'border-cyan-500 text-cyan-500' : 'border-blue-500 text-blue-500'
              }`}>
                <span className="font-bold text-sm">BROKER</span>
              </div>
              
              {/* Language Selector */}
              <div className="relative">
                <button 
                  onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                  data-testid="mobile-language-toggle"
                  aria-label={tx('Language')}
                  className={`px-3 py-2 rounded-full border flex items-center gap-2 ${
                    isDark ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-900'
                  }`}
                >
                  <span className="text-lg">{currentLangOption.flag}</span>
                  <ChevronDown size={14} className={`transition-transform ${showLanguageSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Language Dropdown */}
                {showLanguageSelector && (
                  <div className={`absolute top-full right-0 mt-2 w-40 rounded-xl shadow-xl border z-[100] ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        data-testid={`mobile-language-${lang.code}`}
                        onClick={() => {
                          setLanguage(lang.code);
                          setShowLanguageSelector(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm ${
                          language === lang.code
                            ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-50 text-cyan-600'
                            : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                        } ${lang.code === languageOptions[0].code ? 'rounded-t-xl' : ''} ${lang.code === languageOptions[languageOptions.length - 1].code ? 'rounded-b-xl' : ''}`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Home */}
              <button 
                onClick={() => { setActiveTab('home'); setShowSidebar(false); }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Home size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Home")}</span>
              </button>

              {/* Funds */}
              <button 
                onClick={() => { setActiveTab('funds'); setShowSidebar(false); }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Banknote size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Funds")}</span>
              </button>

              {/* Deposit */}
              <button 
                onClick={() => { setCurrentView('deposit'); setShowSidebar(false); }}
                data-testid="mobile-menu-deposit"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <ArrowDownCircle size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Deposit")}</span>
              </button>

              {/* Withdraw */}
              <button 
                onClick={() => { setCurrentView('withdraw'); setShowSidebar(false); }}
                data-testid="mobile-menu-withdraw"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <ArrowUpCircle size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Withdraw")}</span>
              </button>

              {/* Economic Calendar */}
              <button 
                onClick={() => { setCurrentView('calendar'); setShowSidebar(false); }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Calendar size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Economic Calendar")}</span>
              </button>

              {/* Help Center */}
              <button 
                onClick={() => { setCurrentView('help'); setShowSidebar(false); }}
                className={`w-full flex items-center justify-between px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <HelpCircle size={22} strokeWidth={1.5} />
                  <span className="text-base font-medium">{tx("Help Center")}</span>
                </div>
                <button 
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowHelpSubmenu(!showHelpSubmenu); }}
                  className="p-1"
                >
                  <ChevronDown size={18} className={`transition-transform ${showHelpSubmenu ? 'rotate-180' : ''}`} />
                </button>
              </button>
              
              {/* Help Submenu */}
              {showHelpSubmenu && (
                <div className={`pl-14 pr-5 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <button 
                    onClick={() => { setCurrentView('help'); setShowSidebar(false); }}
                    className={`w-full py-3 text-left text-sm ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {tx("FAQ ")}
                  </button>
                  <button 
                    onClick={() => { setCurrentView('help'); setShowSidebar(false); }}
                    className={`w-full py-3 text-left text-sm ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {tx("Contact Support ")}
                  </button>
                  <button 
                    onClick={() => { setCurrentView('help'); setShowSidebar(false); }}
                    className={`w-full py-3 text-left text-sm ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {tx("Trading Guide ")}
                  </button>
                </div>
              )}

              {/* Personal Settings */}
              <button 
                onClick={() => { setCurrentView('settings'); setShowSidebar(false); }}
                data-testid="mobile-menu-settings"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Settings size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Personal Settings")}</span>
              </button>

              {/* Upload Documents */}
              <button 
                onClick={() => { setCurrentView('documents'); setShowSidebar(false); }}
                data-testid="mobile-menu-documents"
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Upload size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Upload documents")}</span>
              </button>

              {/* Divider */}
              <div className={`my-4 mx-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />

              {/* Theme Toggle */}
              <button 
                onClick={() => { toggleTheme && toggleTheme(); }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                {isDark ? <Sun size={22} strokeWidth={1.5} /> : <Moon size={22} strokeWidth={1.5} />}
                <span className="text-base font-medium">{tx(isDark ? 'Light Mode' : 'Dark Mode')}</span>
              </button>

              {/* Log Out */}
              <button 
                onClick={() => { 
                  if (logout) logout();
                  setShowSidebar(false);
                }}
                className={`w-full flex items-center gap-4 px-5 py-4 ${
                  isDark ? 'text-white hover:bg-gray-800' : 'text-gray-900 hover:bg-gray-50'
                }`}
              >
                <LogOut size={22} strokeWidth={1.5} />
                <span className="text-base font-medium">{tx("Log out")}</span>
              </button>
            </div>

            {/* Share the App Section */}
            <div className={`px-5 py-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <p className={`text-center text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Share the app ")}</p>
              <div className="flex justify-center gap-8">
                <button className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                  <Twitter size={24} />
                </button>
                <button className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                  <Linkedin size={24} />
                </button>
                <button className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
                  <Facebook size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* Overlay with Close Button */}
          <div 
            className="flex-1 bg-black/50 relative"
            onClick={() => setShowSidebar(false)}
          >
            <button 
              onClick={() => setShowSidebar(false)}
              data-testid="mobile-sidebar-close"
              aria-label={tx('Close')}
              className="absolute top-4 right-4 p-2 text-white"
            >
              <X size={28} />
            </button>
          </div>
        </div>
      )}

      {/* Sidebar Animation Styles */}
      <style>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default MobileTraderLayout;
